import React, { useEffect, useMemo } from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createTimeslot,
  updateTimeslot,
} from "../redux/actions/timeslotActions";
import { getBranches } from "../redux/actions/branchActions";
import { timeslotActions } from "../redux/slices/timeslot";
import { cleanFormData } from "../helpers/formHelpers";

import { TimeslotResponseDto } from "../shared/dtos/timeslot/TimeslotResponseDto";
import { TimeslotRequestDto } from "../shared/dtos/timeslot/TimeslotRequestDto";

const timeslotSchema = Yup.object().shape({
  branchId: Yup.string().required("Branch is required"),
  dayOfWeek: Yup.string().required("Day of week is required"),
  startTime: Yup.string().required("Start time is required"),
  endTime: Yup.string().required("End time is required"),
});

const dayOptions = [
  { value: "MONDAY", label: "Monday" },
  { value: "TUESDAY", label: "Tuesday" },
  { value: "WEDNESDAY", label: "Wednesday" },
  { value: "THURSDAY", label: "Thursday" },
  { value: "FRIDAY", label: "Friday" },
  { value: "SATURDAY", label: "Saturday" },
  { value: "SUNDAY", label: "Sunday" },
];

interface CreateOrUpdateTimeslotFormProps {
  timeslot?: TimeslotResponseDto | null;
  userInfo?: any;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateTimeslotForm: React.FC<CreateOrUpdateTimeslotFormProps> = ({
  timeslot = null,
  userInfo = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { items: branches } = useAppSelector((state) => state.branch);

  const isSuperAdmin = userInfo?.userRole === "SUPER_ADMIN";

  useEffect(() => {
    if (isSuperAdmin && (!branches || branches.length === 0)) {
      dispatch(getBranches());
    }
  }, [dispatch, branches, isSuperAdmin]);

  const branchOptions = useMemo(
    () => branches?.map((b) => ({ value: b.id, label: b.name })) || [],
    [branches],
  );

  const initialValues: TimeslotRequestDto = {
    branchId:
      timeslot?.branchId || (isSuperAdmin ? "" : userInfo?.branchId) || "",
    dayOfWeek: timeslot?.dayOfWeek || "MONDAY",
    startTime: timeslot?.startTime ? timeslot.startTime.substring(0, 5) : "",
    endTime: timeslot?.endTime ? timeslot.endTime.substring(0, 5) : "",
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(timeslotActions.setErrorUpdate(null))
            : () => dispatch(timeslotActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={timeslotSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          let startTime = rawCleaned.startTime;
          let endTime = rawCleaned.endTime;

          // Format time string to HH:mm:ss if received as HH:mm
          if (startTime && startTime.length === 5) {
            startTime += ":00";
          }
          if (endTime && endTime.length === 5) {
            endTime += ":00";
          }

          const cleanedValues: TimeslotRequestDto = {
            branchId: rawCleaned.branchId,
            dayOfWeek: rawCleaned.dayOfWeek,
            startTime,
            endTime,
          };

          if (isEditing && timeslot) {
            dispatch(
              updateTimeslot({ id: timeslot.id, values: cleanedValues }),
            );
          } else {
            dispatch(createTimeslot(cleanedValues));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<TimeslotRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              {isSuperAdmin && (
                <FormSelectField
                  name="branchId"
                  label="Branch"
                  options={branchOptions}
                />
              )}

              <FormSelectField
                name="dayOfWeek"
                label="Day of the Week"
                options={dayOptions}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="startTime"
                  label="Start Time"
                  type="time"
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { step: 60 },
                  }}
                />
                <FormTextField
                  name="endTime"
                  label="End Time"
                  type="time"
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { step: 60 },
                  }}
                />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Timeslot"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateTimeslotForm;
